/**
 * Modal for side-by-side comparison of two label versions
 * Supports 3-table architecture (Dec 2025): FDA current, FDA historical, and user drafts
 */
import React, { useEffect, useState, useMemo } from 'react';
import { authApi } from '../services/authApi';
import type { VersionCompareDetailResponse, SectionDiff, VersionSource, PolymorphicCompareResponse } from '../types';
import SplContentRenderer from './SplContentRenderer';
import HybridDiffViewer, { hasActualTextChanges } from './HybridDiffViewer';

interface PolymorphicParams {
  sourceA: VersionSource;
  idA: number;
  sourceB: VersionSource;
  idB: number;
}

interface VersionComparisonModalProps {
  versionIdA?: number;
  versionIdB?: number;
  onClose: () => void;
  isDrugComparison?: boolean; // If true, compare drugs (FDA historical) instead of label_versions
  comparisonData?: VersionCompareDetailResponse | null; // Pre-loaded comparison data (for draft comparison)
  polymorphicParams?: PolymorphicParams | null; // New: 3-table polymorphic comparison params
  /** Render as a BODY, not a modal: no backdrop, no own header, no footer.
   *
   *  Added so the one Version Comparison shell can host this XML<->XML view
   *  as the body for an XML/XML pair while owning the version pickers
   *  itself. Default false, so every existing caller is byte-identical —
   *  this adds a mode, it does not change one. */
  embedded?: boolean;
}

type FilterType = 'all' | 'modified' | 'added' | 'removed' | 'unchanged' | 'renumbered';

/** Backdrop + frame, or nothing when the host already supplies both.
 *
 *  MODULE SCOPE on purpose. Defined inside the component it would be a new
 *  component type on every render, so React would unmount and remount the
 *  entire comparison subtree each time — losing expanded sections and
 *  re-running the fetch effect continuously. */
const Shell = ({ embedded, children }: {
  embedded: boolean; children: React.ReactNode;
}) => (
  embedded
    ? <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    : (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col">
          {children}
        </div>
      </div>
    )
);

const VersionComparisonModal: React.FC<VersionComparisonModalProps> = ({
  versionIdA,
  versionIdB,
  onClose,
  isDrugComparison = false,
  comparisonData = null,
  polymorphicParams = null,
  embedded = false,
}) => {
  const [comparison, setComparison] = useState<VersionCompareDetailResponse | null>(null);
  const [polymorphicComparison, setPolymorphicComparison] = useState<PolymorphicCompareResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Check if we're using polymorphic comparison (3-table architecture)
  const isPolymorphicComparison = polymorphicParams !== null;

  useEffect(() => {
    const fetchComparison = async () => {
      // If comparison data is pre-loaded (e.g., from draft comparison), use it
      if (comparisonData) {
        setComparison(comparisonData as VersionCompareDetailResponse);
        // Auto-expand modified sections by default (with null guard)
        const sectionDiffs = (comparisonData as any).section_diffs ?? [];
        const modifiedSections = new Set<string>(
          sectionDiffs
            .filter((s: SectionDiff) => s.change_type === 'modified')
            .map((s: SectionDiff) => s.section_type)
        );
        setExpandedSections(modifiedSections);
        setIsLoading(false);
        return;
      }

      // NEW: Use polymorphic API for 3-table architecture
      if (polymorphicParams) {
        setIsLoading(true);
        setError(null);
        try {
          const data = await authApi.compareAnyVersions(
            polymorphicParams.sourceA,
            polymorphicParams.idA,
            polymorphicParams.sourceB,
            polymorphicParams.idB
          );
          setPolymorphicComparison(data);
          // Auto-expand modified sections by default (with null guard)
          const sections = data.sections ?? [];
          const modifiedSections = new Set<string>(
            sections
              .filter((s: any) => s.status === 'modified')
              .map((s: any) => s.section_key)
          );
          setExpandedSections(modifiedSections);
        } catch (err: any) {
          // Handle error detail which may be a string or object
          const rawDetail = err.response?.data?.detail;
          const errorDetail = typeof rawDetail === 'object'
            ? (rawDetail?.message || JSON.stringify(rawDetail))
            : (rawDetail || 'Failed to load comparison');
          const status = err.response?.status;
          if (status === 404) {
            setError(`Version not found: ${errorDetail}. The version may have been deleted or the data may be stale. Please refresh the page and try again.`);
          } else {
            setError(errorDetail);
          }
          console.error('[VersionComparisonModal] Polymorphic compare failed:', {
            params: polymorphicParams,
            status,
            error: errorDetail
          });
        } finally {
          setIsLoading(false);
        }
        return;
      }

      // Otherwise fetch the comparison using legacy APIs
      if (!versionIdA || !versionIdB) {
        setError('Missing version IDs for comparison');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        // Use different API based on whether we're comparing drugs or label_versions
        const data = isDrugComparison
          ? await authApi.compareDrugVersions(versionIdA, versionIdB)
          : await authApi.compareVersionsDetailed(versionIdA, versionIdB);
        setComparison(data);
        // Auto-expand modified sections by default (with null guard)
        const sectionDiffs = data.section_diffs ?? [];
        const modifiedSections = new Set<string>(
          sectionDiffs
            .filter((s: SectionDiff) => s.change_type === 'modified')
            .map((s: SectionDiff) => s.section_type)
        );
        setExpandedSections(modifiedSections);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load comparison');
      } finally {
        setIsLoading(false);
      }
    };

    fetchComparison();
  }, [versionIdA, versionIdB, isDrugComparison, comparisonData, polymorphicParams]);

  const filteredSections = useMemo(() => {
    // Handle polymorphic comparison (3-table architecture)
    if (polymorphicComparison) {
      // Guard against undefined/null sections array
      const rawSections = polymorphicComparison.sections ?? [];
      const sections = rawSections.map(s => {
        // Reclassify "modified" sections with no actual text changes as "unchanged"
        // This prevents showing confusing "Modified" badges for XML-only changes
        let effectiveChangeType = s.status as 'added' | 'modified' | 'removed' | 'unchanged' | 'renumbered' | 'renumbered_modified' | 'replaced';
        if (effectiveChangeType === 'modified' || effectiveChangeType === 'renumbered_modified') {
          const oldContent = s.content_xml_a || s.content_a || '';
          const newContent = s.content_xml_b || s.content_b || '';
          if (!hasActualTextChanges(oldContent, newContent)) {
            // No actual text changes - reclassify as unchanged (or just renumbered if it was renumbered_modified)
            effectiveChangeType = effectiveChangeType === 'renumbered_modified' ? 'renumbered' : 'unchanged';
          }
        }
        return {
          section_type: s.section_key ?? 'unknown',
          title: s.title,
          change_type: effectiveChangeType,
          content_a: s.content_a,
          content_b: s.content_b,
          content_xml_a: s.content_xml_a,
          content_xml_b: s.content_xml_b,
          change_description: s.change_description,
          moved_from: s.moved_from,
          moved_to: s.moved_to,
        };
      });
      if (filter === 'all') return sections;
      // For 'renumbered' filter, include both renumbered and renumbered_modified
      if (filter === 'renumbered') {
        return sections.filter(s => s.change_type === 'renumbered' || s.change_type === 'renumbered_modified');
      }
      return sections.filter(s => s.change_type === filter);
    }
    // Handle legacy comparison
    if (!comparison) return [];
    // Guard against undefined section_diffs
    const sectionDiffs = comparison.section_diffs ?? [];
    if (filter === 'all') return sectionDiffs;
    return sectionDiffs.filter(s => s.change_type === filter);
  }, [comparison, polymorphicComparison, filter]);

  // Get active comparison data (polymorphic or legacy)
  const activeComparison = polymorphicComparison || comparison;
  const comparisonStats = polymorphicComparison
    ? polymorphicComparison.summary
    : comparison
      ? {
          total_sections: comparison.total_sections,
          sections_added: comparison.sections_added,
          sections_modified: comparison.sections_modified,
          sections_removed: comparison.sections_removed,
          sections_unchanged: comparison.sections_unchanged,
        }
      : null;

  const toggleSection = (sectionType: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionType)) {
        newSet.delete(sectionType);
      } else {
        newSet.add(sectionType);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    if (polymorphicComparison) {
      const sections = polymorphicComparison.sections ?? [];
      setExpandedSections(new Set(sections.map(s => s.section_key)));
    } else if (comparison) {
      const sectionDiffs = comparison.section_diffs ?? [];
      setExpandedSections(new Set(sectionDiffs.map(s => s.section_type)));
    }
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  const getChangeTypeStyles = (changeType: string) => {
    switch (changeType) {
      case 'added':
        return 'bg-green-50 border-green-200 text-green-800';
      case 'removed':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'modified':
        return 'bg-amber-50 border-amber-200 text-amber-800';
      case 'renumbered':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      case 'renumbered_modified':
        return 'bg-purple-50 border-purple-200 text-purple-800';
      case 'replaced':
        return 'bg-orange-50 border-orange-200 text-orange-800';
      default:
        return 'bg-slate-50 border-slate-200 text-slate-600';
    }
  };

  const getChangeTypeBadge = (changeType: string, changeDescription?: string | null) => {
    switch (changeType) {
      case 'added':
        return <span className="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-700">Added</span>;
      case 'removed':
        return <span className="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700">Removed</span>;
      case 'modified':
        return <span className="px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700">Modified</span>;
      case 'renumbered':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700">
            Renumbered {changeDescription && <span className="font-normal">• {changeDescription}</span>}
          </span>
        );
      case 'renumbered_modified':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700">
            Renumbered + Modified {changeDescription && <span className="font-normal">• {changeDescription}</span>}
          </span>
        );
      case 'replaced':
        return (
          <span className="px-2 py-0.5 text-xs font-medium rounded bg-orange-100 text-orange-700">
            Replaced {changeDescription && <span className="font-normal">• {changeDescription}</span>}
          </span>
        );
      default:
        return <span className="px-2 py-0.5 text-xs font-medium rounded bg-slate-100 text-slate-600">Unchanged</span>;
    }
  };

  return (
    <Shell embedded={embedded}>
        {/* Header */}
        {!embedded && (
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50 rounded-t-lg">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Version Comparison</h2>
            {activeComparison && (
              <p className="text-sm text-slate-600 mt-1">
                Comparing v{activeComparison.version_a.version_number} → v{activeComparison.version_b.version_number}
                {isPolymorphicComparison && polymorphicComparison && (
                  <span className="ml-2 text-xs text-indigo-600">
                    ({polymorphicComparison.version_a.source} vs {polymorphicComparison.version_b.source})
                  </span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        )}

        {/* Loading / Error states */}
        {isLoading && (
          <div className="flex-1 flex items-center justify-center p-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-slate-600">Loading comparison...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex-1 flex items-center justify-center p-12">
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        {activeComparison && comparisonStats && !isLoading && !error && (
          <>
            {/* Stats Bar */}
            <div className="px-6 py-3 bg-white border-b border-slate-200 flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4 text-sm flex-wrap">
                <span className="text-slate-600">
                  <strong>{comparisonStats.total_sections}</strong> sections total
                </span>
                <span className="text-green-600">
                  <strong>{comparisonStats.sections_added}</strong> added
                </span>
                <span className="text-amber-600">
                  <strong>{comparisonStats.sections_modified}</strong> modified
                </span>
                <span className="text-red-600">
                  <strong>{comparisonStats.sections_removed}</strong> removed
                </span>
                {/* Show renumbered count if available (polymorphic comparison) */}
                {(comparisonStats.sections_renumbered || 0) + (comparisonStats.sections_renumbered_modified || 0) > 0 && (
                  <span className="text-blue-600">
                    <strong>{(comparisonStats.sections_renumbered || 0) + (comparisonStats.sections_renumbered_modified || 0)}</strong> renumbered
                  </span>
                )}
                <span className="text-slate-500">
                  <strong>{comparisonStats.sections_unchanged}</strong> unchanged
                </span>
              </div>

              <div className="flex items-center gap-3">
                {/* Filter buttons */}
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                  {(['all', 'modified', 'added', 'removed', 'renumbered', 'unchanged'] as FilterType[]).map((f) => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                        filter === f
                          ? 'bg-white text-indigo-700 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {f.charAt(0).toUpperCase() + f.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Expand/Collapse all */}
                <button
                  onClick={expandAll}
                  className="px-3 py-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded"
                >
                  Expand All
                </button>
                <button
                  onClick={collapseAll}
                  className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded"
                >
                  Collapse All
                </button>
              </div>
            </div>

            {/* Version Headers */}
            <div className="grid grid-cols-2 gap-px bg-slate-200">
              <div className="bg-indigo-50 px-6 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-indigo-900">
                    Version {activeComparison.version_a.version_number}
                  </span>
                  {activeComparison.version_a.status && (
                    <span className="text-xs text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded">
                      {activeComparison.version_a.status}
                    </span>
                  )}
                  {isPolymorphicComparison && polymorphicComparison && (
                    <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                      {polymorphicComparison.version_a.source.replace('_', ' ')}
                    </span>
                  )}
                </div>
                <p className="text-xs text-indigo-700 mt-1">
                  {isPolymorphicComparison && polymorphicComparison
                    ? (polymorphicComparison.version_a as any).brand_name || 'FDA Label'
                    : isDrugComparison && comparison
                      ? (comparison.version_a as any).brand_name || (comparison.version_a as any).generic_name || 'FDA Label'
                      : comparison
                        ? `${comparison.comments_in_a} comments${comparison.version_a.original_filename ? ` • ${comparison.version_a.original_filename}` : ''}`
                        : ''
                  }
                </p>
              </div>
              <div className="bg-indigo-50 px-6 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-indigo-900">
                    Version {activeComparison.version_b.version_number}
                  </span>
                  {activeComparison.version_b.status && (
                    <span className="text-xs text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded">
                      {activeComparison.version_b.status}
                    </span>
                  )}
                  {isPolymorphicComparison && polymorphicComparison && (
                    <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                      {polymorphicComparison.version_b.source.replace('_', ' ')}
                    </span>
                  )}
                </div>
                <p className="text-xs text-indigo-700 mt-1">
                  {isPolymorphicComparison && polymorphicComparison
                    ? (polymorphicComparison.version_b as any).brand_name || 'FDA Label'
                    : isDrugComparison && comparison
                      ? (comparison.version_b as any).brand_name || (comparison.version_b as any).generic_name || 'FDA Label'
                      : comparison
                        ? `${comparison.comments_in_b} comments${comparison.version_b.original_filename ? ` • ${comparison.version_b.original_filename}` : ''}`
                        : ''
                  }
                </p>
              </div>
            </div>

            {/* Sections List */}
            <div className="flex-1 overflow-y-auto">
              {filteredSections.length === 0 ? (
                <div className="p-12 text-center text-slate-500">
                  No sections match the selected filter.
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {filteredSections.map((section, index) => {
                    const isExpanded = expandedSections.has(section.section_type);
                    // Use index as fallback for uniqueness when section_type might collide
                    const uniqueKey = `${section.section_type}-${index}`;
                    return (
                      <div key={uniqueKey} className={`${getChangeTypeStyles(section.change_type)}`}>
                        {/* Section Header */}
                        <button
                          onClick={() => toggleSection(section.section_type)}
                          className="w-full px-6 py-3 flex items-center justify-between hover:bg-black/5 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <span className={`text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                              ▶
                            </span>
                            <span className="font-medium text-sm">
                              {section.title || section.section_type}
                            </span>
                            {getChangeTypeBadge(section.change_type, section.change_description)}
                          </div>
                        </button>

                        {/* Section Content (Side by Side) */}
                        {isExpanded && (
                          <div className="p-4 bg-white">
                            {/* For modified sections, use HybridDiffViewer for rich formatting + accurate diff */}
                            {/* Version A (left) = old/previous, Version B (right) = new/current */}
                            {/* oldContent shows red strikethrough for removed text, newContent shows green for added */}
                            {(section.change_type === 'modified' || section.change_type === 'renumbered_modified' || section.change_type === 'replaced') ? (
                              <HybridDiffViewer
                                oldContentXml={section.content_xml_a || null}
                                oldContentText={section.content_a}
                                newContentXml={section.content_xml_b || null}
                                newContentText={section.content_b}
                                oldContentHtml={(section as any).content_html_a || null}
                                newContentHtml={(section as any).content_html_b || null}
                                drugName={(polymorphicComparison?.version_a as any)?.brand_name || 'Drug'}
                                className="text-xs"
                              />
                            ) : (
                              /* For unchanged/added/removed sections, use SplContentRenderer for rich formatting */
                              <div className="grid grid-cols-2 gap-px bg-slate-300">
                                {/* Version A Content */}
                                <div className={`bg-white p-4 ${!section.content_a && !section.content_xml_a && !(section as any).content_html_a ? 'bg-slate-50' : ''}`}>
                                  {section.content_a || section.content_xml_a || (section as any).content_html_a ? (
                                    <div className="prose prose-sm max-w-none text-slate-700 text-xs leading-relaxed max-h-96 overflow-y-auto">
                                      <SplContentRenderer
                                        contentXml={section.content_xml_a || null}
                                        contentText={section.content_a}
                                        contentHtml={(section as any).content_html_a || null}
                                        drugName={(polymorphicComparison?.version_a as any)?.brand_name || 'Drug'}
                                        className="text-xs"
                                      />
                                    </div>
                                  ) : (
                                    <div className="text-sm text-slate-400 italic py-8 text-center">
                                      {section.change_type === 'added' ? 'Not present in this version' : 'No content'}
                                    </div>
                                  )}
                                </div>

                                {/* Version B Content */}
                                <div className={`bg-white p-4 ${!section.content_b && !section.content_xml_b && !(section as any).content_html_b ? 'bg-slate-50' : ''}`}>
                                  {section.content_b || section.content_xml_b || (section as any).content_html_b ? (
                                    <div className="prose prose-sm max-w-none text-slate-700 text-xs leading-relaxed max-h-96 overflow-y-auto">
                                      <SplContentRenderer
                                        contentXml={section.content_xml_b || null}
                                        contentText={section.content_b}
                                        contentHtml={(section as any).content_html_b || null}
                                        drugName={(polymorphicComparison?.version_b as any)?.brand_name || 'Drug'}
                                        className="text-xs"
                                      />
                                    </div>
                                  ) : (
                                    <div className="text-sm text-slate-400 italic py-8 text-center">
                                      {section.change_type === 'removed' ? 'Not present in this version' : 'No content'}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {!embedded && (
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-slate-600 text-white rounded hover:bg-slate-700 transition-colors"
              >
                Close
              </button>
            </div>
            )}
          </>
        )}
    </Shell>
  );
};

export default VersionComparisonModal;
